import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Hover/tap flip card for service teasers.
 * Front: icon, title, tagline, price
 * Back: outcome, description, bestFor, highlights
 */
export default function ServiceFlipCard({
  icon: Icon,
  title,
  tagline,
  price,
  outcome,
  description,
  bestFor,
  highlights = [],
  badge,
  seeMoreLink,
  isActive = false,
  className = '',
  style,
}) {
  const [flipped, setFlipped] = useState(false);
  const [canHover, setCanHover] = useState(true);

  useEffect(() => {
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const toggleFlip = () => {
    if (!canHover) {
      setFlipped((v) => !v);
    }
  };

  return (
    <div
      className={`service-flip relative ${flipped ? 'is-flipped' : ''} ${isActive ? 'is-active' : ''} ${className}`}
      style={style}
      onClick={toggleFlip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setFlipped((v) => !v);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${title} service card${badge ? `, ${badge}` : ''}. ${flipped ? 'Showing details' : 'Showing overview'}. Activate to flip.`}
    >
      {badge && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 text-[11px] font-semibold px-3 py-1 rounded-full text-white whitespace-nowrap pointer-events-none"
          style={{ fontFamily: "'Poppins', sans-serif", backgroundColor: '#ff7a59' }}
        >
          {badge}
        </span>
      )}
      <div className="service-flip-inner">
        <div className="service-flip-face service-flip-front">
          <div
            className="service-flip-icon flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: '#ff7a59' }}
          >
            {Icon && <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />}
          </div>
          <p
            className="text-xs uppercase tracking-wider mb-2"
            style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59', fontWeight: 700 }}
          >
            {tagline}
          </p>
          <h3
            className="service-flip-title font-bold mb-3 text-gray-700"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {title}
          </h3>
          {price ? (
            <p
              className="service-flip-price text-sm md:text-base"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {price}
            </p>
          ) : (
            <p
              className="service-flip-price text-sm md:text-base"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Custom pricing
            </p>
          )}
          <p
            className="mt-6 text-xs font-medium"
            style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
          >
            {canHover ? 'Hover to flip' : 'Tap to flip'}
          </p>
        </div>

        <div className="service-flip-face service-flip-back">
          <div className="flex-1 w-full flex flex-col items-center justify-start overflow-y-auto">
            {outcome && (
              <p
                className="text-sm md:text-base font-medium mb-3"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                {outcome}
              </p>
            )}
            <p
              className="text-sm leading-relaxed mb-4"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929', opacity: 0.85 }}
            >
              {description}
            </p>
            {bestFor && (
              <p
                className="inline-block text-xs px-3 py-1.5 rounded-full bg-white/40 border border-white/50 mb-4"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                {bestFor}
              </p>
            )}
            {highlights.length > 0 && (
              <ul className="flex flex-wrap justify-center gap-2">
                {highlights.map((highlight) => (
                  <li
                    key={highlight}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#ff7a59]/15 border border-[#ff7a59]/25"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                  >
                    {highlight}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {seeMoreLink && (
            <Link
              to={seeMoreLink}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="mt-4 shrink-0 px-6 py-2.5 text-sm font-medium rounded-full text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
            >
              View More
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
